import { Inject, Injectable } from '@angular/core';
import {
  CanActivate,
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
  CanActivateChild,
  Router,
  UrlTree
} from '@angular/router';
import { MsalGuardConfiguration, MsalService, MSAL_GUARD_CONFIG } from '@azure/msal-angular';
import { RedirectRequest } from '@azure/msal-browser';
import { AuthService } from 'src/app/modules/auth';
import { DynamicAsideMenuService } from 'src/app/_metronic/core';
import { authLocalStorageToken, FORBIDDEN_PATH, saveReturnUrl } from '../shared/common/helper';
import { SSOService } from '../sso/sso.service';
import { ApiService } from './api.service';

@Injectable({ providedIn: 'root' })
export class AuthGuard implements CanActivate, CanActivateChild {

  constructor(
    private api: ApiService,
    private sso: SSOService,
    private authService: AuthService,
    private menu: DynamicAsideMenuService,
    private authMsalService: MsalService,
    private router: Router,
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration
  ) { }

  async canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
    const token = JSON.parse(localStorage.getItem(authLocalStorageToken));
    if (!token) {
      /**
       * Đây là đường đi của người dùng bấm link trong mail khi chưa đăng nhập. Phải ghi
       * nhớ state.url trước khi sang Azure AD, nếu không MSAL sẽ trả họ về /callbackmsal
       * (navigateToLoginRequestUrl đang là false) và tính năng trong link bị mất.
       */
      saveReturnUrl(state.url);
      this.loginSSO();
      return false;
    }

    // UserInfo data
    let currentUser = this.authService.currentUserValue;

    // SSO pass then get userInfo
    if (this.sso.hasValidAccessToken && !currentUser) {
      this.api.loadingCustomOn();
      await new Promise((resolve) => {
        this.authService.getUserByToken().subscribe().add(resolve);
      });
      currentUser = this.authService.currentUserValue;
      this.api.loadingCustomOff();
    }

    const accountMsalCount = this.authMsalService.instance.getActiveAccount();

    // Check isAllow
    if (currentUser || !!accountMsalCount) {
      // pass login
      return true;
    } else {
      /**
       * not pass - phiên tại portal không dùng được nữa. Đăng xuất CỤC BỘ rồi mở trang
       * đăng nhập: session SSO của Azure AD được giữ nguyên nên các ứng dụng khác dùng
       * chung tenant không bị đăng xuất lây.
       */
      saveReturnUrl(state.url);
      this.authService.logoutLocal();
      return false;
    }
  }

  /**
   * @description Chặn route theo quyền của người dùng.
   * @description Khi không đủ quyền thì trả về UrlTree của trang 403 thay vì false: trả
   * @description false chỉ huỷ điều hướng và bỏ người dùng lại ở trang trước đó, mà trong
   * @description luồng mở link từ mail trang đó là /callbackmsal - template rỗng, nên họ
   * @description nhìn thấy màn hình trắng và không hiểu chuyện gì xảy ra.
   * @description Dùng UrlTree chứ không phải router.navigate() để Angular chuyển thẳng
   * @description sang điều hướng mới, tránh hai luồng điều hướng chạy chồng nhau.
   * @param childRoute: ActivatedRouteSnapshot
   * @param state: RouterStateSnapshot
   * @return Promise<boolean | UrlTree>
   */
  async canActivateChild(childRoute: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean | UrlTree> {
    // Check permission
    if (!this.menu.menuPermission$.value) {
      await this.menu.loadPermissions();
    }
    const r = childRoute.data;
    const isAllow = this.menu.menuPermission$.value?.find(f => f.controller == r?.screenKey || r?.screenKey === 'allow');
    if (isAllow) {
      return true;
    }

    console.warn('[AuthGuard] Người dùng không có quyền vào màn hình này.', {
      url: state?.url,
      screenKey: r?.screenKey,
    });
    return this.router.parseUrl(FORBIDDEN_PATH);
  }

  public loginSSO() {
    this.api.loadingCustomOn();
    // initCodeFlow
    //this.sso.initImplicitFlow();

    //login with azure msal
    const authRequest = this.msalGuardConfig.authRequest
      ? { ...this.msalGuardConfig.authRequest } as RedirectRequest
      : undefined;

    /**
     * Bắt buộc phải có nhánh error: guard chặn route và bật spinner, nếu loginRedirect
     * thất bại mà không tắt spinner thì người dùng kẹt ở màn hình trắng có spinner quay
     * mãi, không có cách nào thao tác tiếp.
     */
    this.authMsalService.loginRedirect(authRequest).subscribe({
      error: (error) => {
        console.error('[MSAL] loginRedirect từ AuthGuard thất bại.', error);
        // errorMessage() tự tắt loading và hiện popup cho người dùng
        this.api.errorMessage('Không thể chuyển tới trang đăng nhập. Vui lòng tải lại trang!');
      }
    });
  }
}
